<template>
  <section class="pc">
    <!-- AI disclosure banner — only when AI is on AND replies surface to the
         patient automatically. In review mode the patient never sees an AI
         reply (office reviews each draft first), so we hide the banner —
         from the patient's POV it's plain office-only messaging. -->
    <div v-if="aiCommentsEnabled && aiReplyMode !== 'review'" class="pc-disclosure">
      <i class="fa fa-info-circle"></i>
      <span>
        Replies marked from <strong>Office</strong> may be drafted by an AI assistant trained on
        your plan. Your office reviews and updates these as needed.
      </span>
    </div>

    <div class="pc-field">
      <label class="pc-label" for="pc-textarea">Have a question about your plan?</label>
      <textarea
        id="pc-textarea"
        ref="textarea"
        v-model="draft"
        class="pc-textarea"
        rows="3"
        :placeholder="placeholder"
        :disabled="sending"
        :maxlength="MAX_CHARS"
        @keydown.meta.enter.prevent="onSubmit"
        @keydown.ctrl.enter.prevent="onSubmit"
      ></textarea>
      <div class="pc-meta">
        <span class="pc-charcount" :class="{ 'pc-charcount-near': nearLimit }">
          {{ draft.length }} / {{ MAX_CHARS }}
        </span>
        <span class="pc-hint">⌘/Ctrl + Enter to send</span>
      </div>
    </div>

    <div v-if="composeError" class="pc-error">
      <i class="fa fa-exclamation-circle"></i> {{ composeError }}
      <button class="pc-error-dismiss" type="button" @click="onDismissError" title="Dismiss">×</button>
    </div>

    <button
      class="pc-submit"
      :disabled="!canSubmit"
      @click="onSubmit"
    >
      <i v-if="sending" class="fa fa-circle-o-notch fa-spin"></i>
      <i v-else class="fa fa-paper-plane"></i>
      {{ sending ? 'Sending…' : 'Send to office' }}
    </button>

    <p v-if="!aiCommentsEnabled" class="pc-no-ai-note">
      Your office will read your question and get back to you. You'll see their reply here once
      they respond.
    </p>
  </section>
</template>

<script>
/**
 * Slice 8b — patient compose box.
 *
 * Talks to the txPresenter store's postPatientComment action which handles
 * the optimistic insert. This component is responsible only for the input UI,
 * character counting, and the disclosure banner that shows when the AI gates
 * are ON. The AI placeholder ("Office is replying…") lives inside the
 * CommentsThread render path, since it's a thread row.
 */
const MAX_CHARS = 1000;

export default {
  name: 'PatientCompose',
  props: {
    aiCommentsEnabled: { type: Boolean, default: true },
    /**
     * Slice 8d-1 — 'auto' (AI replies visible to patient immediately)
     * vs 'review' (held for office approval, patient never sees the
     * draft directly). Drives whether we show the AI disclosure banner
     * and whether the placeholder copy is "your office will reply" vs
     * "Office will respond shortly".
     */
    aiReplyMode: { type: String, default: 'auto' },
    sending: { type: Boolean, default: false },
    composeError: { type: String, default: '' },
  },
  data() {
    return {
      draft: '',
      MAX_CHARS,
    };
  },
  computed: {
    canSubmit() {
      if (this.sending) return false;
      const trimmed = this.draft.trim();
      return trimmed.length > 0 && trimmed.length <= MAX_CHARS;
    },
    nearLimit() {
      return this.draft.length >= MAX_CHARS - 100;
    },
    placeholder() {
      return this.aiCommentsEnabled
        ? 'Type your question — your office will reply'
        : 'Type your question and your office will get back to you';
    },
  },
  methods: {
    onSubmit() {
      if (!this.canSubmit) return;
      const body = this.draft.trim();
      // Clear the draft optimistically; if the post fails, the action surfaces
      // the error in compose state and the placeholder gets removed by the
      // store. The patient can re-type if needed.
      this.draft = '';
      this.$emit('submit', { body });
    },
    onDismissError() {
      this.$emit('dismiss-error');
    },
  },
};
</script>

<style scoped>
.pc {
  margin-top: 18px;
  padding: 22px 24px;
  background: #ffffff;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
}

/* Disclosure banner */
.pc-disclosure {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 14px;
  padding: 10px 12px;
  background: #f5f3ff;
  border: 1px solid #e0d7fe;
  border-radius: 6px;
  font-size: 12px;
  color: #4c1d95;
  line-height: 1.45;
}
.pc-disclosure .fa { font-size: 14px; color: #6d28d9; flex-shrink: 0; margin-top: 1px; }

/* Compose field */
.pc-field { display: flex; flex-direction: column; }
.pc-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7785;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 8px;
}
.pc-textarea {
  width: 100%;
  padding: 12px 14px;
  font-size: 14px;
  font-family: inherit;
  color: #1f2937;
  border: 1px solid #d4d8de;
  border-radius: 8px;
  background: #fafbfc;
  resize: vertical;
  min-height: 80px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.pc-textarea:focus {
  outline: none;
  border-color: #0d5f54;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(13, 95, 84, 0.12);
}
.pc-textarea:disabled { opacity: 0.6; cursor: not-allowed; }

.pc-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 6px;
  font-size: 11px;
  color: #94a3b8;
}
.pc-charcount-near { color: #d97706; font-weight: 600; }

/* Error banner */
.pc-error {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #991b1b;
  font-size: 13px;
}
.pc-error .fa { color: #dc2626; }
.pc-error-dismiss {
  margin-left: auto;
  background: none;
  border: none;
  color: #991b1b;
  font-size: 18px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
}

/* Submit button */
.pc-submit {
  width: 100%;
  margin-top: 14px;
  padding: 12px 18px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  background: #0d5f54;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease;
}
.pc-submit:hover:not(:disabled) { background: #0a4a42; }
.pc-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.pc-submit .fa { margin-right: 6px; }

.pc-no-ai-note {
  margin: 12px 0 0;
  font-size: 12px;
  color: #6b7785;
  line-height: 1.45;
  text-align: center;
}
</style>
